/**
 * The two values a finished exam gets exactly once (F6 T-2.2): the `idempotencyKey` that lets the
 * server recognise a resend, and the moment it finished. They are attached to the object that
 * represents the finished exam (the sim's `ExamResult`, the theory `ExamState`), so rendering the
 * result again, a StrictMode double effect or a remounted screen reads the same stamp instead of
 * minting a second key (which would write a second row).
 */

export interface AttemptStamp {
  readonly idempotencyKey: string;
  /** ISO 8601, taken when the stamp was made. */
  readonly finishedAt: string;
}

const stamps = new WeakMap<object, AttemptStamp>();

/** A random UUID v4 (`crypto.randomUUID` needs a secure context; `getRandomValues` does not). */
export function newIdempotencyKey(): string {
  const c = globalThis.crypto;
  if (typeof c.randomUUID === 'function') return c.randomUUID();
  const bytes = c.getRandomValues(new Uint8Array(16));
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
  const hex = [...bytes].map((b) => b.toString(16).padStart(2, '0'));
  return [
    hex.slice(0, 4).join(''),
    hex.slice(4, 6).join(''),
    hex.slice(6, 8).join(''),
    hex.slice(8, 10).join(''),
    hex.slice(10).join(''),
  ].join('-');
}

/** The stamp of a finished exam, made on the first call for `owner` and the same afterwards. */
export function attemptStampFor(owner: object, now: () => Date = () => new Date()): AttemptStamp {
  const existing = stamps.get(owner);
  if (existing !== undefined) return existing;
  const stamp: AttemptStamp = {
    idempotencyKey: newIdempotencyKey(),
    finishedAt: now().toISOString(),
  };
  stamps.set(owner, stamp);
  return stamp;
}
